// Typed digital fact-find API client for the web layer (PRD #70).
//
// Wraps the `factFindContract` endpoints via the shared `api` client and
// exposes react-query hooks. Mutations invalidate `factFindKeys.all` so the
// register and the desk review view re-render together.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, apiFetch, staffErrorMessage } from '@/lib/api';
import { downloadBlob } from '@/lib/download';
import { track } from '@/lib/analytics/client';
import { internalReviewKeys } from '@/lib/use-internal-review';
import { suitabilityRefreshKeys } from '@/lib/use-suitability-refresh';
import type {
    ContactFactFind,
    CreateFactFindRequest,
    FactFind,
    FactFindAmendmentDto,
    FactFindAmendmentsResponse,
    FactFindAnswer,
    FactFindAccountGroupOption,
    FactFindClientOption,
    FactFindDocument,
    FactFindRecordOption,
    FactFindRegisterRow,
    FactFindRiskSummaryRow,
    FactFindSigningState,
    FactFindStatus,
    FactFindSuggestionsResponse,
    SuiHistoryResponse,
    PrefillSuggestion,
    SendForSignatureSigner,
} from '@septima/contract';

export const factFindKeys = {
    all: ['fact-find'] as const,
    register: (imId?: string) => ['fact-find', 'register', imId ?? null] as const,
    riskSummary: (imId?: string) => ['fact-find', 'risk-summary', imId ?? null] as const,
    clientOptions: () => ['fact-find', 'client-options'] as const,
    // The Suitability Records a fact find can start on (SEP-353).
    recordOptions: (query: RecordOptionsQuery) =>
        ['fact-find', 'record-options', query.clientId ?? null, query.recordId ?? null] as const,
    // The account groups a new Suitability Record can cover (SEP-353).
    accountGroupOptions: () => ['fact-find', 'account-group-options'] as const,
    // The fact finds of one Contact, for the contact page (SEP-261).
    contactFactFinds: (contactId: string) =>
        ['fact-find', 'contact-fact-finds', contactId] as const,
    detail: (factFindId: string) => ['fact-find', 'detail', factFindId] as const,
    prefill: (factFindId: string) => ['fact-find', 'prefill', factFindId] as const,
    document: (factFindId: string) => ['fact-find', 'document', factFindId] as const,
    signing: (factFindId: string) => ['fact-find', 'signing', factFindId] as const,
    // The evidenced amendments of one version (SEP-243 sections 7 and 42).
    amendments: (factFindId: string, versionId?: string) =>
        ['fact-find', 'amendments', factFindId, versionId ?? null] as const,
    // The suggestion boxes of one draft (SEP-288).
    suggestions: (factFindId: string, versionId: string) =>
        ['fact-find', 'suggestions', factFindId, versionId] as const,
    // The permanent audit history of one SUI (SEP-288).
    history: (factFindId: string, versionId: string) =>
        ['fact-find', 'history', factFindId, versionId] as const,
};

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export function useFactFindRegister(imId?: string) {
    return useQuery({
        queryKey: factFindKeys.register(imId),
        queryFn: async (): Promise<FactFindRegisterRow[]> => {
            const res = await api.factFind.listRegister({ query: imId ? { imId } : {} });
            if (res.status !== 200) throw new Error(`Register failed: ${res.status}`);
            return res.body.rows;
        },
        staleTime: 10_000,
    });
}

/**
 * Per-client fact-find risk summary (band / mandate / total + capacity flags)
 * for the Vulnerable Clients surface. Sourced from the `desk`-readable
 * FactFind. The VulnerabilityRecord gives no risk score, so the page reads the
 * score here.
 */
export function useFactFindRiskSummary(imId?: string) {
    return useQuery({
        queryKey: factFindKeys.riskSummary(imId),
        queryFn: async (): Promise<FactFindRiskSummaryRow[]> => {
            const res = await api.factFind.riskSummary({ query: imId ? { imId } : {} });
            if (res.status !== 200) throw new Error(`Risk summary failed: ${res.status}`);
            return res.body.rows;
        },
        staleTime: 10_000,
    });
}

/** Clients for the manual-create typeahead (identity + has-fact-find flag). */
export function useFactFindClientOptions(options: { enabled?: boolean } = {}) {
    return useQuery({
        queryKey: factFindKeys.clientOptions(),
        enabled: options.enabled ?? true,
        queryFn: async (): Promise<FactFindClientOption[]> => {
            const res = await api.factFind.listClientOptions({ query: {} });
            if (res.status !== 200) throw new Error(`Clients failed: ${res.status}`);
            return res.body.clients;
        },
        staleTime: 30_000,
    });
}

/** The filters of the SUR picker: a client account, or one SUR (SEP-353). */
export interface RecordOptionsQuery {
    readonly clientId?: string;
    readonly recordId?: string;
}

/**
 * The Suitability Records a fact find can start on, with their accounts,
 * contacts and newest fact find (SEP-353). The picker searches the list in
 * the browser (`filterFactFindRecordOptions`), so a keystroke sends no request.
 */
export function useFactFindRecordOptions(
    query: RecordOptionsQuery = {},
    options: { enabled?: boolean } = {},
) {
    return useQuery({
        queryKey: factFindKeys.recordOptions(query),
        enabled: options.enabled ?? true,
        queryFn: async (): Promise<FactFindRecordOption[]> => {
            const res = await api.factFind.listRecordOptions({
                query: {
                    ...(query.clientId ? { clientId: query.clientId } : {}),
                    ...(query.recordId ? { recordId: query.recordId } : {}),
                },
            });
            if (res.status !== 200) throw new Error(`Suitability records failed: ${res.status}`);
            return res.body.rows;
        },
        staleTime: 10_000,
    });
}

/** The account groups a new Suitability Record can cover (SEP-353). */
export function useFactFindAccountGroupOptions(options: { enabled?: boolean } = {}) {
    return useQuery({
        queryKey: factFindKeys.accountGroupOptions(),
        enabled: options.enabled ?? true,
        queryFn: async (): Promise<FactFindAccountGroupOption[]> => {
            const res = await api.factFind.listAccountGroupOptions();
            if (res.status !== 200) throw new Error(`Client accounts failed: ${res.status}`);
            return res.body.groups;
        },
        staleTime: 30_000,
    });
}

/**
 * The fact finds one contact is the subject of, for the contact page
 * (SEP-261). The server names the person with the same rule that the previous
 * answers use.
 */
export function useContactFactFinds(contactId: string) {
    return useQuery({
        queryKey: factFindKeys.contactFactFinds(contactId),
        queryFn: async (): Promise<ContactFactFind[]> => {
            const res = await api.factFind.listContactFactFinds({ query: { contactId } });
            if (res.status !== 200) throw new Error(staffErrorMessage(res.body, res.status));
            return res.body.rows;
        },
        staleTime: 30_000,
    });
}

export function useFactFind(factFindId: string | undefined) {
    return useQuery({
        queryKey: factFindKeys.detail(factFindId ?? ''),
        enabled: Boolean(factFindId),
        queryFn: async (): Promise<FactFind> => {
            const res = await api.factFind.getFactFind({
                params: { factFindId: factFindId ?? '' },
            });
            if (res.status !== 200) throw new Error(staffErrorMessage(res.body, res.status));
            return res.body.factFind;
        },
        staleTime: 5_000,
    });
}

export function useFactFindPrefill(factFindId: string | undefined) {
    return useQuery({
        queryKey: factFindKeys.prefill(factFindId ?? ''),
        enabled: Boolean(factFindId),
        queryFn: async (): Promise<PrefillSuggestion[]> => {
            const res = await api.factFind.listPrefill({
                params: { factFindId: factFindId ?? '' },
            });
            if (res.status !== 200) throw new Error(`Prefill failed: ${res.status}`);
            return res.body.suggestions;
        },
        staleTime: 5_000,
    });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export function useCreateFactFindDraft() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: {
            factFindId: string;
            by: string;
            reason?: string;
            refreshSections?: string[];
        }) => {
            const res = await api.factFind.createDraft({
                params: { factFindId: input.factFindId },
                body: {
                    by: input.by,
                    reason: input.reason ?? 'Initial fact find',
                    refreshSections: input.refreshSections ?? [],
                },
            });
            if (res.status !== 200) throw new Error(staffErrorMessage(res.body, res.status));
            return res.body.factFind;
        },
        onSuccess: () => void qc.invalidateQueries({ queryKey: factFindKeys.all }),
    });
}

/**
 * Manual create (PRD #70 Slice 3): start a fact find "from scratch" for a new
 * client with no account yet. The `clientType` selects the canonical Dowgate
 * form (the active dialog tab). A client with accounts starts on a Suitability
 * Record instead (SEP-353).
 */
export function useCreateFactFind() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (
            input: CreateFactFindRequest,
        ): Promise<{
            clientId: string;
            factFindId: string;
            createdFactFindIds: string[];
            factFind: FactFind;
        }> => {
            const res = await api.factFind.createFactFind({ body: input });
            if (res.status !== 200) throw new Error(staffErrorMessage(res.body, res.status));
            return {
                clientId: res.body.clientId,
                factFindId: res.body.factFindId,
                createdFactFindIds: res.body.createdFactFindIds,
                factFind: res.body.factFind,
            };
        },
        onSuccess: () => void qc.invalidateQueries({ queryKey: factFindKeys.all }),
    });
}

/**
 * Atomic working-version save for the Desk fill editor — commits every edited
 * section plus (optionally) the ATR responses and CFL inputs in a single
 * request, replacing the per-section / ATR / CFL saves the editor used to fire
 * one at a time. The server merges + recomputes exactly as those did.
 */
export function useSaveFactFindWorking() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: {
            factFindId: string;
            by: string;
            sections: Record<string, Record<string, FactFindAnswer>>;
            atr?: Record<string, string>;
            cfl?: {
                annualIncome: number;
                essentialExpenditure: number;
                liquidAssets: number;
                knownCommitments: number;
                portfolioValue: number;
            };
        }) => {
            const res = await api.factFind.saveWorking({
                params: { factFindId: input.factFindId },
                body: {
                    by: input.by,
                    sections: input.sections,
                    atr: input.atr,
                    cfl: input.cfl,
                },
            });
            if (res.status !== 200) throw new Error(staffErrorMessage(res.body, res.status));
            return res.body.factFind;
        },
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: factFindKeys.all });
            // SEP-293. The Internal Review state and the flags read the fact find.
            void qc.invalidateQueries({ queryKey: internalReviewKeys.all });
        },
    });
}

export function useTransitionFactFind() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: {
            factFindId: string;
            to: FactFindStatus;
            by: string;
            note?: string;
        }) => {
            const res = await api.factFind.transition({
                params: { factFindId: input.factFindId },
                body: { to: input.to, by: input.by, note: input.note },
            });
            if (res.status !== 200) throw new Error(staffErrorMessage(res.body, res.status));
            return res.body.factFind;
        },
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: factFindKeys.all });
            // SEP-293. The Internal Review state and the flags read the fact find.
            void qc.invalidateQueries({ queryKey: internalReviewKeys.all });
            // SEP-288. The submission moves the suitability refresh of the SUI
            // to `returned`, and the refresh panel reads the version status.
            void qc.invalidateQueries({ queryKey: suitabilityRefreshKeys.all });
        },
    });
}

/**
 * Confirm the receipt of the client-completed fact find, and freeze the
 * original client answers of the working version (SEP-243 sections 6 and 40).
 *
 * The document itself is uploaded through the client Documents door
 * (`useUploadDocument` with `area: 'fact_find'`). This mutation records the
 * receipt and names that upload as its evidence; it stores no bytes. The
 * document is mandatory, because it is the evidence of the receipt.
 *
 * This is the staff door, and it names no door on the wire. The portal door
 * gets its own endpoint with the portal submit path.
 */
export function useLockFactFindReceipt() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: {
            factFindId: string;
            by: string;
            sourceUploadId: string;
            note?: string;
        }) => {
            const res = await api.factFind.lockReceipt({
                params: { factFindId: input.factFindId },
                body: {
                    by: input.by,
                    sourceUploadId: input.sourceUploadId,
                    note: input.note,
                },
            });
            if (res.status !== 200) throw new Error(staffErrorMessage(res.body, res.status));
            return res.body.factFind;
        },
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: factFindKeys.all });
            // SEP-288. The receipt marks the suitability refresh as returned.
            void qc.invalidateQueries({ queryKey: suitabilityRefreshKeys.all });
        },
    });
}

/**
 * The evidenced amendments of one version, with the folded confirmed answers
 * (SEP-243 sections 7 and 42).
 *
 * The server DERIVES `confirmed` at the mapping seam with the one pure fold, so
 * this hook computes nothing: the panel reads the Original Client Answer and
 * the Current Confirmed Value straight off the response.
 */
export function useFactFindAmendments(factFindId: string, versionId?: string) {
    return useQuery({
        queryKey: factFindKeys.amendments(factFindId, versionId),
        queryFn: async (): Promise<FactFindAmendmentsResponse> => {
            const res = await api.factFind.listAmendments({
                params: { factFindId },
                query: versionId ? { versionId } : {},
            });
            if (res.status !== 200) throw new Error(staffErrorMessage(res.body, res.status));
            return res.body;
        },
        enabled: Boolean(factFindId),
        staleTime: 10_000,
    });
}

/**
 * Record one evidenced correction beside the original client answer.
 *
 * The request names NEITHER the client NOR the original answer: the server
 * reads both from the version, so this call cannot write a figure the client
 * never gave. The original itself never moves (acceptance point 11).
 */
export function useRecordFactFindAmendment() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: {
            factFindId: string;
            by: string;
            versionId: string;
            sectionId: string;
            questionId: string;
            correctedValue: FactFindAnswer;
            reason: string;
            // A member of staff never records `client_portal` (SEP-293).
            sourceKind: Exclude<FactFindAmendmentDto['sourceKind'], 'client_portal'>;
            sourceRef?: string | null;
            sourceUploadIds?: string[];
        }) => {
            const res = await api.factFind.recordAmendment({
                params: { factFindId: input.factFindId },
                body: {
                    by: input.by,
                    versionId: input.versionId,
                    sectionId: input.sectionId,
                    questionId: input.questionId,
                    correctedValue: input.correctedValue,
                    reason: input.reason,
                    sourceKind: input.sourceKind,
                    sourceRef: input.sourceRef ?? null,
                    sourceUploadIds: input.sourceUploadIds,
                },
            });
            if (res.status !== 200) throw new Error(staffErrorMessage(res.body, res.status));
            return res.body;
        },
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: factFindKeys.all });
            // SEP-293. The Internal Review state and the flags read the fact find.
            void qc.invalidateQueries({ queryKey: internalReviewKeys.all });
        },
    });
}

/** Withdraw one amendment. The row stays, and it holds a withdrawal stamp. */
export function useWithdrawFactFindAmendment() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: {
            factFindId: string;
            amendmentId: string;
            by: string;
            reason: string;
        }) => {
            const res = await api.factFind.withdrawAmendment({
                params: { factFindId: input.factFindId, amendmentId: input.amendmentId },
                body: { by: input.by, reason: input.reason },
            });
            if (res.status !== 200) throw new Error(staffErrorMessage(res.body, res.status));
            return res.body;
        },
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: factFindKeys.all });
            // SEP-293. The Internal Review state and the flags read the fact find.
            void qc.invalidateQueries({ queryKey: internalReviewKeys.all });
        },
    });
}

/**
 * Soft-delete a fact find (DESK 9). The record is retained in Postgres but
 * hidden from the register + detail. Invalidates every fact-find query so the
 * register drops the row and the detail view reads as "not found".
 */
export function useDeleteFactFind() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: { factFindId: string; by: string; reason?: string }) => {
            const res = await api.factFind.deleteFactFind({
                params: { factFindId: input.factFindId },
                body: { by: input.by, reason: input.reason },
            });
            if (res.status !== 200) throw new Error(staffErrorMessage(res.body, res.status));
            return res.body;
        },
        onSuccess: () => void qc.invalidateQueries({ queryKey: factFindKeys.all }),
    });
}

/**
 * An administrator deletes a fact find and the rows that exist only for it
 * (SEP-355). The server refuses each persona that is not admin, and a fact
 * find that a rule keeps (409). The SUR of the fact find stays, so its reads
 * refresh too.
 */
/**
 * The fact find is a record of advice (an approved SUI, a final approval or a
 * signed PDF). The admin must confirm the delete again.
 */
export class FactFindRecordOfAdviceError extends Error {}

export function useAdminDeleteFactFind() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: { factFindId: string; confirmKept?: boolean }) => {
            const res = await api.factFind.adminDeleteFactFind({
                params: { factFindId: input.factFindId },
                body: input.confirmKept ? { confirmKept: true } : {},
            });
            if (res.status === 409 && res.body.code === 'fact_find_delete_refused') {
                throw new FactFindRecordOfAdviceError(res.body.error);
            }
            if (res.status !== 200) throw new Error(staffErrorMessage(res.body, res.status));
            return res.body;
        },
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: factFindKeys.all });
            void qc.invalidateQueries({ queryKey: internalReviewKeys.all });
            void qc.invalidateQueries({ queryKey: ['suitability-records'] });
        },
    });
}

/**
 * The prior answers and the client-record changes of one draft (SEP-288). The
 * form shows each prior answer in a box beside its field.
 */
export function useFactFindSuggestions(
    factFindId: string,
    versionId: string,
    options?: { enabled?: boolean },
) {
    return useQuery({
        queryKey: factFindKeys.suggestions(factFindId, versionId),
        enabled: options?.enabled ?? true,
        queryFn: async (): Promise<FactFindSuggestionsResponse> => {
            const res = await api.factFind.getSuggestions({
                params: { factFindId },
                query: { versionId },
            });
            if (res.status !== 200) throw new Error(`Suggestions failed: ${res.status}`);
            return res.body;
        },
        staleTime: 30_000,
    });
}

/** The permanent audit history of one SUI, oldest first (SEP-288). */
export function useSuiHistory(factFindId: string, versionId: string) {
    return useQuery({
        queryKey: factFindKeys.history(factFindId, versionId),
        queryFn: async (): Promise<SuiHistoryResponse> => {
            const res = await api.factFind.getSuiHistory({ params: { factFindId, versionId } });
            if (res.status !== 200) throw new Error(`History failed: ${res.status}`);
            return res.body;
        },
        staleTime: 10_000,
    });
}

/**
 * Create a new V6 fact find with a new SUI (SEP-288, SEP-353): on one fact
 * find, or on THE fact find of one SUR. A SUR with no fact find gets one. The
 * server admits the assigned IM or EA only for a refresh.
 */
export function useCreateNewFactFind() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: {
            factFindId?: string;
            suitabilityRecordId?: string;
            by: string;
        }) => {
            const res = await api.factFind.createNewFactFind({ body: input });
            if (res.status !== 200) throw new Error(staffErrorMessage(res.body, res.status));
            return res.body.factFindIds;
        },
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: factFindKeys.all });
            void qc.invalidateQueries({ queryKey: ['suis'] });
        },
    });
}

export function useAddFactFindAnnotation() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: {
            factFindId: string;
            sectionId: string;
            note: string;
            kind: 'note' | 'gap' | 'inconsistency';
            by: string;
        }) => {
            const res = await api.factFind.addAnnotation({
                params: { factFindId: input.factFindId },
                body: {
                    by: input.by,
                    sectionId: input.sectionId,
                    note: input.note,
                    kind: input.kind,
                },
            });
            if (res.status !== 200) throw new Error(staffErrorMessage(res.body, res.status));
            return res.body.factFind;
        },
        onSuccess: () => void qc.invalidateQueries({ queryKey: factFindKeys.all }),
    });
}

export function useRefreshFactFind() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: {
            factFindId: string;
            sections: string[];
            message: string;
            by: string;
        }) => {
            const res = await api.factFind.refresh({
                params: { factFindId: input.factFindId },
                body: {
                    by: input.by,
                    sections: input.sections,
                    message: input.message,
                },
            });
            if (res.status !== 200) throw new Error(staffErrorMessage(res.body, res.status));
            return res.body;
        },
        onSuccess: () => void qc.invalidateQueries({ queryKey: factFindKeys.all }),
    });
}

export function useAcceptPrefill() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: { suggestionId: string; by: string; factFindId?: string }) => {
            const res = await api.factFind.acceptPrefill({
                params: { suggestionId: input.suggestionId },
                // The handle names WHICH fact find of the client takes the
                // value (SEP-261). A suggestion belongs to the client, and a
                // joint client holds several fact finds.
                body: { by: input.by, factFindId: input.factFindId },
            });
            if (res.status !== 200) throw new Error(staffErrorMessage(res.body, res.status));
            return res.body;
        },
        onSuccess: () => void qc.invalidateQueries({ queryKey: factFindKeys.all }),
    });
}

export function useRejectPrefill() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: { suggestionId: string; by: string }) => {
            const res = await api.factFind.rejectPrefill({
                params: { suggestionId: input.suggestionId },
                body: { by: input.by },
            });
            if (res.status !== 200) throw new Error(staffErrorMessage(res.body, res.status));
            return res.body;
        },
        onSuccess: () => void qc.invalidateQueries({ queryKey: factFindKeys.all }),
    });
}

// ---------------------------------------------------------------------------
// Generated document (PRD #70 Slice 7)
// ---------------------------------------------------------------------------

/** The generated fact-find document meta (no PDF bytes), or null if none yet. */
export function useFactFindDocument(factFindId: string | undefined) {
    return useQuery({
        queryKey: factFindKeys.document(factFindId ?? ''),
        enabled: Boolean(factFindId),
        queryFn: async (): Promise<FactFindDocument | null> => {
            const res = await api.factFind.getDocument({
                params: { factFindId: factFindId ?? '' },
            });
            if (res.status !== 200) throw new Error(staffErrorMessage(res.body, res.status));
            return res.body.document;
        },
        staleTime: 5_000,
    });
}

/** Generate / regenerate the filled fact-find PDF; returns the document meta. */
export function useGenerateFactFindDocument() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: { factFindId: string; by: string }) => {
            const res = await api.factFind.generateDocument({
                params: { factFindId: input.factFindId },
                body: { by: input.by },
            });
            if (res.status !== 200) throw new Error(staffErrorMessage(res.body, res.status));
            return res.body.document;
        },
        onSuccess: (_doc, input) =>
            void qc.invalidateQueries({ queryKey: factFindKeys.document(input.factFindId) }),
    });
}

/**
 * Download the filled PDF: fetch the binary route (credentials included) and
 * trigger a browser download. Returns once the download has been kicked off.
 * The binary route is not part of the ts-rest client, so this uses `apiFetch`.
 */
export async function downloadFilledFactFindPdf(
    factFindId: string,
    clientName: string,
): Promise<void> {
    return downloadFactFindPdf('filled', factFindId, clientName);
}

/** Download the signed PDF (DocuSign combined doc or manually-uploaded copy). */
export async function downloadSignedFactFindPdf(
    factFindId: string,
    clientName: string,
): Promise<void> {
    return downloadFactFindPdf('signed', factFindId, clientName);
}

/**
 * Fetch the filled / signed Fact Find PDF as a Blob (credentials included).
 * Shared by the download helpers and the live split-view preview. The binary
 * route is not part of the ts-rest client, so this uses `apiFetch`.
 */
export async function fetchFactFindPdfBlob(
    kind: 'filled' | 'signed',
    factFindId: string,
): Promise<Blob> {
    const res = await apiFetch(`/api/fact-find/${encodeURIComponent(factFindId)}/document/${kind}`);
    if (!res.ok) {
        throw new Error(
            res.status === 404 ? `No ${kind} PDF yet` : `Preview failed (${res.status})`,
        );
    }
    return res.blob();
}

/** Shared binary-PDF download for the filled / signed routes. */
async function downloadFactFindPdf(
    kind: 'filled' | 'signed',
    factFindId: string,
    clientName: string,
): Promise<void> {
    track('interaction', {
        details: { action: 'export_pdf', document: `fact-find-${kind}` },
        objectType: 'fact-find',
        objectId: factFindId,
    });
    const blob = await fetchFactFindPdfBlob(kind, factFindId);
    downloadBlob(
        blob,
        `fact-find-${kind === 'signed' ? 'signed-' : ''}${clientName || factFindId}.pdf`,
    );
}

// ---------------------------------------------------------------------------
// Signing (PRD #70 Slice 9) — DocuSign send + manual fallback
// ---------------------------------------------------------------------------

/** Signing state for the client fact-find view (gates the DocuSign affordance). */
export function useFactFindSigning(factFindId: string | undefined) {
    return useQuery({
        queryKey: factFindKeys.signing(factFindId ?? ''),
        enabled: Boolean(factFindId),
        queryFn: async (): Promise<FactFindSigningState> => {
            const res = await api.factFind.getSigningState({
                params: { factFindId: factFindId ?? '' },
            });
            if (res.status !== 200) throw new Error(staffErrorMessage(res.body, res.status));
            return res.body.signing;
        },
        staleTime: 5_000,
    });
}

/** Send the filled fact-find PDF for DocuSign signature. */
export function useSendForSignature() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: {
            factFindId: string;
            by: string;
            signers: SendForSignatureSigner[];
            emailSubject?: string;
        }) => {
            const res = await api.factFind.sendForSignature({
                params: { factFindId: input.factFindId },
                body: { by: input.by, signers: input.signers, emailSubject: input.emailSubject },
            });
            if (res.status !== 200) throw new Error(staffErrorMessage(res.body, res.status));
            return res.body.signing;
        },
        onSuccess: (_s, input) => {
            void qc.invalidateQueries({ queryKey: factFindKeys.signing(input.factFindId) });
            void qc.invalidateQueries({ queryKey: factFindKeys.document(input.factFindId) });
        },
    });
}

/** Mark the fact-find document signed (manual, without an uploaded copy). */
export function useMarkFactFindSigned() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: { factFindId: string; by: string }) => {
            const res = await api.factFind.markSigned({
                params: { factFindId: input.factFindId },
                body: { by: input.by },
            });
            if (res.status !== 200) throw new Error(staffErrorMessage(res.body, res.status));
            return res.body.signing;
        },
        onSuccess: (_s, input) => {
            void qc.invalidateQueries({ queryKey: factFindKeys.signing(input.factFindId) });
            void qc.invalidateQueries({ queryKey: factFindKeys.document(input.factFindId) });
        },
    });
}

/**
 * Upload a signed PDF (manual fallback). The multipart route is not part of the
 * ts-rest client, so this posts FormData via `apiFetch`.
 */
export function useUploadSignedFactFind() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: { factFindId: string; file: File }) => {
            const form = new FormData();
            form.append('file', input.file);
            const res = await apiFetch(
                `/api/fact-find/${encodeURIComponent(input.factFindId)}/document/upload-signed`,
                { method: 'POST', body: form },
            );
            if (!res.ok) {
                let message = `Upload failed (${res.status})`;
                try {
                    const body = (await res.json()) as { message?: string; error?: string };
                    message = body.message ?? body.error ?? message;
                } catch {
                    // keep the default message
                }
                throw new Error(message);
            }
            return (await res.json()) as { ok: true };
        },
        onSuccess: (_r, input) => {
            void qc.invalidateQueries({ queryKey: factFindKeys.signing(input.factFindId) });
            void qc.invalidateQueries({ queryKey: factFindKeys.document(input.factFindId) });
        },
    });
}

// ---------------------------------------------------------------------------
// Client-email draft (per-user advisory-draft path, Outlook)
// ---------------------------------------------------------------------------

interface MailboxStatus {
    configured: boolean;
    connected: boolean;
    email: string | null;
    connectedAt?: string | null;
}

/**
 * Connection status of the signed-in user's Outlook (Microsoft 365). Gates the
 * draft affordance — mirrors the Settings panel's query.
 */
export function useOutlookStatus() {
    return useQuery<MailboxStatus>({
        queryKey: ['integration', 'outlook', 'status'],
        queryFn: async () => {
            const res = await apiFetch('/api/outlook/status', { cache: 'no-store' });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            return (await res.json()) as MailboxStatus;
        },
        staleTime: 30_000,
    });
}

/**
 * Draft a client cover-note email into the IM's Outlook mailbox. Returns the
 * draft id + a deep link so the caller can open it for review + sending. The
 * server defaults the mailbox provider to Outlook.
 */
export function useDraftClientEmail() {
    return useMutation({
        mutationFn: async (input: { factFindId: string; by: string }) => {
            const res = await api.factFind.draftClientEmail({
                params: { factFindId: input.factFindId },
                body: { by: input.by },
            });
            if (res.status !== 200) throw new Error(staffErrorMessage(res.body, res.status));
            return res.body;
        },
    });
}
